import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ModalShell, ModalBody } from '../Modal';
import { Button } from '../Button';

interface ModalProps {
  isVisible: boolean;
  title: string;
  message: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'secondary' | 'danger';
  showCancel?: boolean;
  /** 입력해야 확인 버튼이 활성화되는 정확한 문자열 (예: 회사명) */
  confirmKeyword?: string;
  /** 입력란 위에 표시되는 안내 문구 */
  confirmPrompt?: string;
}

const PromptLabel = styled.p`
  margin: ${({ theme }) => theme.spacing[4]}px 0 ${({ theme }) => theme.spacing[2]}px;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-line;
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]}px ${({ theme }) => theme.spacing[3]}px;
  border: 1px solid #d0d0d0;
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  font-size: ${({ theme }) => theme.fontSize.md}px;
  color: ${({ theme }) => theme.colors.text.primary};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.button.bg.primary.default};
  }
`;

export const Modal: React.FC<ModalProps> = ({
  isVisible,
  title,
  message,
  onConfirm,
  onCancel,
  onClose,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  // variant is accepted for API compatibility but styling is deferred to button defaults
  variant: _variant = 'primary',
  showCancel = true,
  confirmKeyword,
  confirmPrompt,
}) => {
  const [typed, setTyped] = useState('');

  const requiresKeyword = Boolean(confirmKeyword);
  const isConfirmDisabled = requiresKeyword && typed.trim() !== confirmKeyword;

  const handleConfirm = () => {
    if (isConfirmDisabled) return;
    onConfirm?.();
    onClose?.();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  return (
    <ModalShell
      isOpen={isVisible}
      title={title}
      onClose={() => onClose?.()}
      size="sm"
      showCloseButton={false}
      footer={
        <>
          {showCancel && (
            <Button variant="secondary" onClick={handleCancel}>
              {cancelText}
            </Button>
          )}
          <Button variant="primary" onClick={handleConfirm} disabled={isConfirmDisabled}>
            {confirmText}
          </Button>
        </>
      }
    >
      <ModalBody style={{ whiteSpace: 'pre-line' }}>{message}</ModalBody>
      {requiresKeyword && (
        <>
          {confirmPrompt && <PromptLabel>{confirmPrompt}</PromptLabel>}
          <ConfirmInput
            type="text"
            value={typed}
            placeholder={confirmKeyword}
            autoComplete="off"
            autoFocus
            onChange={e => setTyped(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleConfirm();
            }}
          />
        </>
      )}
    </ModalShell>
  );
};
